import { STATUS_ORDER } from "@/components/prosthesis/prosthesis-meta";
import type { ServiceRow } from "./production-types";
import { rotuloDoMaterialDoServico } from "@/components/prosthesis/rotulo-do-material";

export type SortField =
  | "none"
  // Campos do PEDIDO
  | "createdAt"
  | "deadline"
  | "status"
  | "dentist"
  | "clinic"
  | "patient"
  // Campos do SERVIÇO (a linha da planilha é um serviço, não um pedido)
  | "service"
  | "assignedTo"
  | "stage"
  | "modelStage"
  | "material"
  | "shade"
  | "teeth"
  | "notes"
  | "attachment";

export type SortDirection = "asc" | "desc";

export const SORT_FIELD_LABELS: Record<SortField, string> = {
  none: "Nenhum",
  createdAt: "Data do pedido",
  deadline: "Prazo",
  status: "Status",
  dentist: "Dentista",
  clinic: "Clínica",
  patient: "Paciente",
  service: "Serviço",
  assignedTo: "Responsável",
  stage: "Estágio",
  modelStage: "Modelo",
  material: "Material",
  shade: "Cor",
  teeth: "Dentes",
  notes: "Observações",
  attachment: "Anexo",
};

export const SORT_FIELDS: SortField[] = [
  "none",
  "createdAt",
  "deadline",
  "status",
  "stage",
  "modelStage",
  "service",
  "assignedTo",
  "clinic",
  "dentist",
  "patient",
  "teeth",
  "material",
  "shade",
  "notes",
  "attachment",
];

export const SORT_LEVEL_LABELS = ["1ª", "2ª", "3ª"];

/** Vazio sempre no fim, independente da direção pedida — "sem responsável" no topo da lista
 *  crescente seria ruído, não informação. `￿` é o maior code point comparável. */
const VAZIO = "￿";

function compareDates(a: Date | string | null, b: Date | string | null): number {
  if (!a && !b) return 0;
  if (!a) return 1;
  if (!b) return -1;
  return new Date(a).getTime() - new Date(b).getTime();
}

function texto(a: string | null | undefined, b: string | null | undefined): number {
  return (a || VAZIO).localeCompare(b || VAZIO, "pt-BR", { numeric: true });
}

function materialDe(row: ServiceRow): string | null {
  return rotuloDoMaterialDoServico(row.service);
}

function compareByField(field: SortField, a: ServiceRow, b: ServiceRow): number {
  switch (field) {
    case "createdAt":
      return compareDates(a.order.createdAt, b.order.createdAt);
    case "deadline":
      return compareDates(a.order.deadline, b.order.deadline);
    // O status exibido na linha é o do SERVIÇO (o do pedido é só o rollup dele) — pedido sem
    // serviço cai pro rollup pra não ficar sempre no fim.
    case "status":
      return (
        STATUS_ORDER.indexOf(a.service?.status ?? a.order.status) -
        STATUS_ORDER.indexOf(b.service?.status ?? b.order.status)
      );
    case "dentist":
      return texto(a.order.dentistName, b.order.dentistName);
    case "clinic":
      return texto(a.order.organization.name, b.order.organization.name);
    case "patient":
      return texto(a.order.patientName, b.order.patientName);
    case "service":
      return texto(a.service?.serviceCategory.label, b.service?.serviceCategory.label);
    case "assignedTo":
      return texto(a.service?.assignedToUser?.name, b.service?.assignedToUser?.name);
    case "stage":
      return texto(a.service?.productionStage?.label, b.service?.productionStage?.label);
    case "modelStage":
      return texto(a.service?.modelStage?.label, b.service?.modelStage?.label);
    case "material":
      return texto(materialDe(a), materialDe(b));
    case "shade":
      return texto(a.service?.shadeOption?.code, b.service?.shadeOption?.code);
    case "teeth":
      return texto(a.service?.teeth, b.service?.teeth);
    case "notes":
      return texto(a.service?.notes, b.service?.notes);
    // Anexo não tem o que ordenar alfabeticamente: o que interessa é quem TEM anexo.
    case "attachment":
      return Number(!!b.service?.attachmentFile) - Number(!!a.service?.attachmentFile);
    default:
      return 0;
  }
}

export function makeComparator(criteria: SortField[], directions: SortDirection[]) {
  return (a: ServiceRow, b: ServiceRow) => {
    for (let i = 0; i < criteria.length; i++) {
      const field = criteria[i];
      if (field === "none") continue;
      const result = compareByField(field, a, b);
      if (result !== 0) return directions[i] === "desc" ? -result : result;
    }
    return 0;
  };
}
